import React, { useEffect, useState, useCallback } from "react";
import { FileText, Check, X, Clock, MapPin, Users } from "lucide-react";
import { api } from "../lib/api";

/**
 * CustomOffersPanel — Customer-side view of incoming Custom Offers from
 * concierges. Customer can Accept (booking → Accepted, awaiting payment)
 * or Decline. Hidden if there are no pending offers.
 *
 * Preview Mode: Accept/Decline actions fire normally; the global preview
 * write-guard returns 403 and the Sign-up modal pops via the axios
 * interceptor. No special-case handling needed here.
 */
export default function CustomOffersPanel({ onChange }) {
  const [offers, setOffers] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/offers").then((r) => setOffers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOffers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = offers.filter((o) => o.status === "pending");
  if (pending.length === 0) return null;

  const act = async (offerId, action) => {
    setBusyId(offerId);
    try {
      await api.post(`/offers/${offerId}/${action}`);
      await load();
      onChange?.();
    } catch (e) {
      // Preview/permission errors handled by global axios interceptor.
      console.error("Failed to update offer", e);
    }
    setBusyId(null);
  };

  return (
    <section className="mt-12" data-testid="customer-custom-offers">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl flex items-center gap-2">
          <FileText size={18} className="text-[#C9A45C]"/>
          Custom Offers
        </h2>
        <span className="text-xs text-[#071A2D]/55">{pending.length} pending</span>
      </div>
      <div className="space-y-3">
        {pending.map((o) => (
          <div
            key={o.offer_id}
            data-testid={`offer-card-${o.offer_id}`}
            className="rounded-3xl card-luxury-elevated p-5 ring-1 ring-[#C9A45C]/40"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">
                  Custom Offer · From {o.provider?.name || "your concierge"}
                </div>
                <div className="font-serif text-xl mt-1.5">{o.title}</div>
                {o.overview && (
                  <p className="text-sm text-[#071A2D]/72 mt-2 leading-relaxed">{o.overview}</p>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#071A2D]/65 mt-3">
                  {o.booking?.date && (
                    <span className="inline-flex items-center gap-1"><Clock size={11}/> {o.booking.date} · {o.booking.time}</span>
                  )}
                  {o.booking?.guests && (
                    <span className="inline-flex items-center gap-1"><Users size={11}/> {o.booking.guests} guests</span>
                  )}
                  {o.booking?.location && (
                    <span className="inline-flex items-center gap-1"><MapPin size={11}/> {o.booking.location}</span>
                  )}
                </div>
                {(o.included_services || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {o.included_services.map((s) => (
                      <span key={s} className="text-[11px] px-2.5 py-0.5 rounded-full bg-[#064E3B]/8 text-[#064E3B] font-medium">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                {o.timeline && (
                  <details className="mt-3 text-xs text-[#071A2D]/65">
                    <summary className="cursor-pointer font-semibold text-[#064E3B]">Itinerary</summary>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-xs leading-relaxed">{o.timeline}</pre>
                  </details>
                )}
                <div className="text-[11px] text-[#071A2D]/55 mt-3">
                  Cancellation policy: <span className="font-semibold text-[#071A2D]/80">{o.cancellation_policy}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-[#071A2D]/55">Total</div>
                <div className="font-serif text-2xl text-[#071A2D] mt-0.5">
                  ${Number(o.price || 0).toLocaleString()}
                </div>
                {o.deposit_percent > 0 && (
                  <div className="text-[11px] text-[#071A2D]/60 mt-1">
                    {o.deposit_percent}% deposit (${Number(o.deposit_amount || 0).toLocaleString()})
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#071A2D]/8 flex items-center justify-end gap-2">
              <button
                onClick={() => act(o.offer_id, "decline")}
                disabled={busyId === o.offer_id}
                data-testid={`offer-decline-${o.offer_id}`}
                className="btn-outline-emerald text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <X size={12}/> Decline
              </button>
              <button
                onClick={() => act(o.offer_id, "accept")}
                disabled={busyId === o.offer_id}
                data-testid={`offer-accept-${o.offer_id}`}
                className="btn-coral text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Check size={12}/> Accept offer
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
